/**
 * CollapsibleSection — Collapsible card section for FullPageDetailTemplate
 *
 * Matches OnMove-Admin OverviewSection / PermissionsSection pattern:
 *  - Card with glassmorphism styling
 *  - Clickable header with icon, title, badge count, expand/collapse chevron
 *  - Collapse transition for content
 *
 * Props:
 *   title       — section heading
 *   icon        — MUI icon element for header
 *   iconGradient — [startColor, endColor] for icon background
 *   badge       — optional number/string shown as a count badge
 *   expanded    — controlled expand state
 *   onToggle    — toggle callback
 *   children    — section content
 */

import PropTypes from "prop-types";
import { Card, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import { useMaterialUIController } from "context";

function CollapsibleSection({
  title,
  icon,
  iconGradient,
  badge,
  expanded,
  onToggle,
  children,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [gradStart, gradEnd] = iconGradient || ["#0D9488", "#5EEAD4"];

  return (
    <Card
      sx={{
        mb: 2,
        overflow: "hidden",
        background: darkMode
          ? "rgba(255,255,255,0.03)"
          : "rgba(255,255,255,0.9)",
        backdropFilter: "blur(10px)",
        border: darkMode
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid rgba(0,0,0,0.05)",
        borderRadius: "16px",
      }}
    >
      {/* Header — clickable */}
      <KDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2.5}
        py={2}
        onClick={onToggle}
        sx={{
          cursor: "pointer",
          "&:hover": {
            background: darkMode
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.02)",
          },
        }}
      >
        <KDBox display="flex" alignItems="center" gap={1.5}>
          {/* Icon pill */}
          <KDBox
            sx={{
              background: `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)`,
              borderRadius: "10px",
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 36,
              minHeight: 36,
            }}
          >
            {icon}
          </KDBox>

          <KDTypography
            variant="h6"
            fontWeight="bold"
            color={darkMode ? "white" : "dark"}
          >
            {title}
          </KDTypography>

          {badge != null && (
            <KDBox
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 24,
                height: 24,
                px: 1,
                borderRadius: "12px",
                fontSize: "0.75rem",
                fontWeight: 700,
                bgcolor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
              }}
            >
              {badge}
            </KDBox>
          )}
        </KDBox>

        <IconButton
          size="small"
          sx={{ color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.4)" }}
        >
          <ExpandMoreIcon
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          />
        </IconButton>
      </KDBox>

      {/* Content */}
      <Collapse in={expanded}>
        <KDBox px={2.5} pb={2.5} pt={1}>
          {children}
        </KDBox>
      </Collapse>
    </Card>
  );
}

CollapsibleSection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  iconGradient: PropTypes.arrayOf(PropTypes.string),
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node,
};

CollapsibleSection.defaultProps = {
  icon: null,
  iconGradient: null,
  badge: null,
  children: null,
};

export default CollapsibleSection;
